import React from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { PerformanceEnrichedRow, PerformanceSortKey } from '../../types';
import { formatCurrency } from 'utils';

interface Props {
  rows: PerformanceEnrichedRow[];
  sortKey: PerformanceSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (key: PerformanceSortKey) => void;
}

interface ColumnDef {
  key: PerformanceSortKey;
  label: string;
  group: 'deals' | 'penetration' | 'profit' | 'summary';
  format: 'num' | 'pct' | 'dollar';
  borderRight?: boolean;
  minWidth?: number;
}

const DATA_COLUMNS: ColumnDef[] = [
  { key: 'deals', label: 'Total', group: 'deals', format: 'num', minWidth: 60 },
  { key: 'fiDeals', label: 'F&I', group: 'deals', format: 'num', borderRight: true, minWidth: 60 },
  { key: 'financePenetration', label: 'Finance', group: 'penetration', format: 'pct', minWidth: 90 },
  { key: 'escPenetration', label: 'Warranty (VSC)', group: 'penetration', format: 'pct', minWidth: 110 },
  { key: 'insurancePenetration', label: 'Insurance', group: 'penetration', format: 'pct', minWidth: 90 },
  { key: 'gapPenetration', label: 'GAP', group: 'penetration', format: 'pct', minWidth: 75 },
  {
    key: 'roadsidePenetration',
    label: 'Roadside',
    group: 'penetration',
    format: 'pct',
    borderRight: true,
    minWidth: 90,
  },
  { key: 'financeProfit', label: 'Finance', group: 'profit', format: 'dollar', minWidth: 100 },
  { key: 'escProfit', label: 'Warranty (VSC)', group: 'profit', format: 'dollar', minWidth: 110 },
  { key: 'insuranceProfit', label: 'Insurance', group: 'profit', format: 'dollar', minWidth: 100 },
  { key: 'gapProfit', label: 'GAP', group: 'profit', format: 'dollar', minWidth: 85 },
  { key: 'roadsideProfit', label: 'Roadside', group: 'profit', format: 'dollar', borderRight: true, minWidth: 100 },
  { key: 'totalProfit', label: 'Total profit', group: 'summary', format: 'dollar', minWidth: 110 },
  { key: 'avgProfitPerAllDeals', label: 'Avg / deal', group: 'summary', format: 'dollar', minWidth: 130 },
];

const PEN_KEYS: PerformanceSortKey[] = [
  'financePenetration',
  'escPenetration',
  'insurancePenetration',
  'gapPenetration',
  'roadsidePenetration',
];

const BORDER_SX = { borderRight: '1px solid', borderColor: 'divider' };

const HEADER_CELL_SX = {
  fontSize: 11,
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
  py: 1,
  px: 1,
};

const GROUP_HEADER_SX = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: 'text.disabled',
  textAlign: 'center',
  py: 0.75,
  px: 1,
};

function penBarColor(pct: number): string {
  if (pct >= 70) return '#4caf50';
  if (pct >= 40) return '#ff9800';
  return '#ef5350';
}

function rankBadgeSx(rank: number) {
  const colors: Record<number, { bg: string; color: string }> = {
    1: { bg: '#e8f5e9', color: '#2e7d32' },
    2: { bg: '#e3f2fd', color: '#1565c0' },
    3: { bg: '#fff8e1', color: '#f57f17' },
  };
  return colors[rank];
}

export const PerformanceTable = ({ rows, sortKey, sortDir, onSort }: Props) => {
  const penMaxValues = React.useMemo(
    () =>
      PEN_KEYS.reduce(
        (acc, key) => {
          acc[key] = Math.max(...rows.map(r => r[key] as number), 1);
          return acc;
        },
        {} as Record<PerformanceSortKey, number>
      ),
    [rows]
  );

  const rankMap = React.useMemo(() => {
    const sorted = [...rows].sort((a, b) => (b[sortKey] as number) - (a[sortKey] as number));
    return sorted.slice(0, 3).reduce(
      (acc, r, i) => {
        acc[r.name] = i + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [rows, sortKey]);

  const totalDeals = rows.reduce((s, r) => s + r.deals, 0);
  const totalFiDeals = rows.reduce((s, r) => s + r.fiDeals, 0);
  const totalProfit = rows.reduce((s, r) => s + r.totalProfit, 0);

  const createSortHandler = (key: PerformanceSortKey) => () => onSort(key);

  const renderHeaderCell = (col: ColumnDef) => (
    <TableCell
      key={col.key}
      align="right"
      sx={{
        ...HEADER_CELL_SX,
        ...(col.borderRight ? BORDER_SX : {}),
        minWidth: col.minWidth,
        bgcolor: col.group === 'penetration' || col.group === 'summary' ? 'grey.50' : 'background.paper',
      }}
    >
      <TableSortLabel
        active={sortKey === col.key}
        direction={sortKey === col.key ? sortDir : 'desc'}
        onClick={createSortHandler(col.key)}
        sx={{ fontSize: 'inherit', '& .MuiTableSortLabel-icon': { fontSize: 12 } }}
      >
        {col.label}
      </TableSortLabel>
    </TableCell>
  );

  const renderBodyCell = (col: ColumnDef, row: PerformanceEnrichedRow) => {
    const value = row[col.key] as number;
    const borderSx = col.borderRight ? BORDER_SX : {};

    if (col.format === 'pct') {
      const maxVal = penMaxValues[col.key] ?? 1;
      const barWidth = Math.round((value / maxVal) * 76);
      const color = penBarColor(value);
      return (
        <TableCell
          key={col.key}
          align="right"
          sx={{
            ...borderSx,
            py: 1,
            px: 1,
            backgroundImage: `linear-gradient(to right, ${alpha(color, 0.15)} 0%, ${alpha(color, 0.15)} ${barWidth}%, transparent ${barWidth}%)`,
            backgroundSize: 'calc(100% - 16px) 24px',
            backgroundPosition: '8px center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {value}%
        </TableCell>
      );
    }

    if (col.key === 'avgProfitPerAllDeals') {
      return (
        <TableCell key={col.key} align="right" sx={{ ...borderSx, py: 1, px: 1 }}>
          <Box sx={{ fontSize: 13, fontWeight: 500 }}>{formatCurrency(row.avgProfitPerAllDeals, true)}</Box>
          <Box sx={{ fontSize: 11, color: 'text.secondary' }}>{formatCurrency(row.avgProfitPerFIDeals, true)} F&I</Box>
        </TableCell>
      );
    }

    if (col.format === 'dollar') {
      return (
        <TableCell
          key={col.key}
          align="right"
          sx={{
            ...borderSx,
            py: 1,
            px: 1,
            fontWeight: col.key === 'totalProfit' ? 700 : 400,
            color: col.key === 'totalProfit' ? 'primary.main' : 'inherit',
          }}
        >
          {formatCurrency(value, true)}
        </TableCell>
      );
    }

    return (
      <TableCell
        key={col.key}
        align="right"
        sx={{ ...borderSx, py: 1, px: 1, color: col.key === 'deals' ? 'text.disabled' : 'inherit' }}
      >
        {value}
      </TableCell>
    );
  };

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 480 }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1520 }}>
          <TableHead>
            <TableRow>
              <TableCell
                rowSpan={2}
                align="left"
                sx={{ ...HEADER_CELL_SX, bgcolor: 'grey.50', minWidth: 160, zIndex: 4 }}
              >
                Name
              </TableCell>
              <TableCell colSpan={2} sx={{ ...GROUP_HEADER_SX, ...BORDER_SX, bgcolor: 'background.paper' }}>
                Deals
              </TableCell>
              <TableCell colSpan={5} sx={{ ...GROUP_HEADER_SX, ...BORDER_SX, bgcolor: 'grey.50' }}>
                Penetration
              </TableCell>
              <TableCell colSpan={5} sx={{ ...GROUP_HEADER_SX, ...BORDER_SX, bgcolor: 'background.paper' }}>
                Profit ($)
              </TableCell>
              <TableCell colSpan={2} sx={{ ...GROUP_HEADER_SX, bgcolor: 'grey.50' }}>
                Summary
              </TableCell>
            </TableRow>
            <TableRow>{DATA_COLUMNS.map(renderHeaderCell)}</TableRow>
          </TableHead>

          <TableBody>
            {rows.map(row => {
              const rank = rankMap[row.name];
              return (
                <TableRow key={row.name} hover>
                  <TableCell
                    align="left"
                    sx={{ py: 1, px: 1, fontWeight: 600, borderRight: '1px solid', borderColor: 'divider' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {rank ? (
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            fontWeight: 700,
                            flexShrink: 0,
                            bgcolor: rankBadgeSx(rank).bg,
                            color: rankBadgeSx(rank).color,
                          }}
                        >
                          {rank}
                        </Box>
                      ) : (
                        <Box sx={{ width: 20 }} />
                      )}
                      {row.name}
                    </Box>
                  </TableCell>
                  {DATA_COLUMNS.map(col => renderBodyCell(col, row))}
                </TableRow>
              );
            })}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'text.secondary',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                Totals ({rows.length} {rows.length === 1 ? 'row' : 'rows'})
              </TableCell>
              {DATA_COLUMNS.map(col => {
                const borderSx = col.borderRight ? BORDER_SX : {};
                const isActive = sortKey === col.key;
                const sx = {
                  fontSize: 13,
                  fontWeight: 700,
                  textAlign: 'right',
                  ...(isActive ? { color: 'primary.main' } : {}),
                  ...borderSx,
                  bgcolor: 'grey.50',
                };

                if (col.format === 'pct') {
                  const avg = rows.length
                    ? Math.round(rows.reduce((s, r) => s + (r[col.key] as number), 0) / rows.length)
                    : 0;
                  return (
                    <TableCell key={col.key} sx={sx}>
                      {avg}%
                    </TableCell>
                  );
                }
                if (col.key === 'deals')
                  return (
                    <TableCell key={col.key} sx={{ ...sx, color: 'text.disabled' }}>
                      {totalDeals}
                    </TableCell>
                  );
                if (col.key === 'fiDeals')
                  return (
                    <TableCell key={col.key} sx={sx}>
                      {totalFiDeals}
                    </TableCell>
                  );
                if (col.key === 'totalProfit')
                  return (
                    <TableCell key={col.key} sx={{ ...sx, color: 'primary.main' }}>
                      {formatCurrency(totalProfit, true)}
                    </TableCell>
                  );
                if (col.key === 'avgProfitPerAllDeals')
                  return (
                    <TableCell key={col.key} sx={{ ...sx, textAlign: 'right' }}>
                      <Box>{formatCurrency(totalDeals ? Math.round(totalProfit / totalDeals) : 0, true)}</Box>
                      <Box sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 400 }}>
                        {formatCurrency(totalFiDeals ? Math.round(totalProfit / totalFiDeals) : 0, true)} F&I
                      </Box>
                    </TableCell>
                  );
                const sum = rows.reduce((s, r) => s + (r[col.key] as number), 0);
                return (
                  <TableCell key={col.key} sx={sx}>
                    {formatCurrency(sum, true)}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableFooter>
        </Table>
      </Box>
    </TableContainer>
  );
};
